import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Spinner } from 'react-activity';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  adminDailyCallsService,
  callsService,
  decodedToken,
  profilesService,
  sessionStorageService,
  userService,
} from '@project/services';
import { StorageKey } from '@project/helpers/storage-keys';
import { useDownloadFile } from '@project/hooks/global';
import {
  CallOutcome,
  CallOutcomeDescriptionEnum,
  CallOutcomeFormFields,
  EditCallCentreProfileFormFields,
  FinishCallProps,
} from '@project/types';
import { Button, ConditionalRenderer } from '@project/components';
import {
  CallOutcomeForm,
  EditCallCentreProfileForm,
} from '@project/components/forms/call-centre-forms';
import { dateTimeFormatterGmtZoneOffset } from '@project/helpers';

const CALL_OUTCOME_BADGE_STYLES: Partial<Record<CallOutcome, string>> = {
  [CallOutcome.ClientWillPay]: 'bg-green-100 text-green-800',
  [CallOutcome.DataUpdated]: 'bg-green-100 text-green-800',
  [CallOutcome.AmendedEmailAddress]: 'bg-green-100 text-green-800',
  [CallOutcome.CallBackLater]: 'bg-primary bg-opacity-10 text-primary-dark',
  [CallOutcome.NoMoney]: 'bg-red-100 text-red-800',
  [CallOutcome.WrongNumber]: 'bg-red-100 text-red-800',
  [CallOutcome.DoesNotExist]: 'bg-red-100 text-red-800',
  [CallOutcome.NotInService]: 'bg-red-100 text-red-800',
  [CallOutcome.NoFines]: 'bg-red-100 text-red-800',
  [CallOutcome.Unknown]: 'bg-red-100 text-red-800',
};

const callOutcomeBadgeStyle = (outcome: CallOutcome) =>
  CALL_OUTCOME_BADGE_STYLES[outcome] ?? 'bg-grey-brand-3 text-text-grey';

const formatCallOutcome = (outcome: CallOutcome) => {
  const label = CallOutcome[outcome];

  if (!label) return 'Unknown';

  return label.replace(/([A-Z])/g, ' $1').trim();
};

type PanelProps = {
  title: string;
  children: React.ReactNode;
  className?: string;
  allowOverflow?: boolean;
};

const Panel: React.FC<PanelProps> = ({ title, children, className = '', allowOverflow }) => (
  <div
    className={`flex flex-col bg-white rounded-lg shadow-md ${
      allowOverflow ? '' : 'overflow-hidden'
    } ${className}`}
  >
    <div className="rounded-t-lg bg-secondary px-4 py-2.5 text-white text-xs font-semibold uppercase tracking-wider">
      {title}
    </div>
    {children}
  </div>
);

const PanelPlaceholder: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-grow items-center justify-center p-6 text-xs text-text-grey text-center">
    {children}
  </div>
);

export const CallCentreOrganism: React.FC = () => {
  const [isCreateCallLoading, setIsCreateCallLoading] = useState(false);
  const [isProfileUpdateLoading, setIsProfileUpdateLoading] = useState(false);

  const userId = decodedToken.decodedTokenUserId();
  const queryClient = useQueryClient();
  const [newCallId, setNewCallId] = useState(
    sessionStorageService.getItem(StorageKey.CurrentCallId),
  );
  const [pageNumber, setPageNumber] = useState(1);

  const { download: downloadCallLeftReport, loading: isLoadingCallLeftReport } = useDownloadFile({
    apiDefinition: () => adminDailyCallsService.getAllDailyCallsContent(userId),
    onError: () => toast.error('Could not download report'),
  });
  const currentDailyCallId = sessionStorageService.getItem(StorageKey.CurrentDailyCallId) as string;

  const { data: agentNextCallData, isFetching: isFetchingNextCall } = currentDailyCallId
    ? useQuery('dailyCall', () => adminDailyCallsService.getById(currentDailyCallId), {
        refetchOnWindowFocus: false,
      })
    : useQuery('agentNextCall', adminDailyCallsService.getAgentNextCall, {
        refetchOnWindowFocus: false,
      });

  const { data: callContactCallsData, isFetching: isFetchingCallHistory } = useQuery(
    ['callContactCallHistory', pageNumber, agentNextCallData?.callContactId],
    () => callsService.getCalls(pageNumber, 10, null, agentNextCallData?.callContactId),
    {
      enabled: !!agentNextCallData?.userId,
    },
  );

  const { data: agentDailyCallsTotalData } = useQuery(
    'agentDailyCallsTotal',
    () => callsService.getCalls(1, 1, userId, null, new Date(new Date().setUTCHours(0, 0, 0, 0))),
    {
      enabled: !!userId,
    },
  );

  const _createCall = () => {
    setIsCreateCallLoading(true);

    callsService.createCall(agentNextCallData?.callContactId, userId).then((id) => {
      sessionStorageService.setItem(StorageKey.CurrentDailyCallId, agentNextCallData.dailyCallId);
      sessionStorageService.setItem(StorageKey.CurrentCallId, id);
      setNewCallId(id);

      setIsCreateCallLoading(false);

      toast.success('Started new call successfully.');
    });
  };

  const _finishCall = (co: CallOutcomeFormFields) => {
    setIsCreateCallLoading(true);

    const finishCallProps: FinishCallProps = {
      callContactId: agentNextCallData.callContactId,
      callId: newCallId as string,
      callOutcome: +co.callOutcome,
      durationInSeconds: co.durationInSeconds,
      doNotContact: co.doNotContact,
      callBackDate: co.callBack ? co.callBackDate : null,
    };

    callsService.finishCall(finishCallProps).then(() => {
      queryClient.invalidateQueries('agentNextCall');
      queryClient.invalidateQueries('callContactCallHistory');
      queryClient.invalidateQueries('agentDailyCallsTotal');

      setNewCallId(null);
      setIsCreateCallLoading(false);
      sessionStorageService.removeItem(StorageKey.CurrentDailyCallId);
      sessionStorageService.removeItem(StorageKey.CurrentCallId);

      toast.success('Call completed successfully.');
    });
  };

  const _updateProfile = async (account: EditCallCentreProfileFormFields) => {
    setIsProfileUpdateLoading(true);

    return profilesService.editCallCenterAccount(account).then(() => {
      setIsProfileUpdateLoading(false);
      toast.success('Contact updated successfully.');
    });
  };

  const deletePortalUserMutation = useMutation(
    () => userService.deletePortalUserAccount(agentNextCallData?.userId),
    {
      onSuccess: () => {
        toast.success('User deleted successfully.');
      },
      onError: () => {
        toast.error('An error occurred deleting Portal User');
        setIsCreateCallLoading(false);
      },
    },
  );

  const _deleteProfile = () => {
    if (window.confirm('Are you sure you wish to delete this account?')) {
      setIsCreateCallLoading(true);

      const finishCallProps: FinishCallProps = {
        callContactId: agentNextCallData.callContactId,
        callId: newCallId as string,
        callOutcome: 999,
        durationInSeconds: 0,
        doNotContact: true,
        callBackDate: null,
      };

      callsService.finishCall(finishCallProps).then(() => {
        queryClient.invalidateQueries('agentNextCall');
        queryClient.invalidateQueries('callContactCallHistory');
        queryClient.invalidateQueries('agentDailyCallsTotal');

        setNewCallId(null);
        setIsCreateCallLoading(false);
        sessionStorageService.removeItem(StorageKey.CurrentDailyCallId);
        sessionStorageService.removeItem(StorageKey.CurrentCallId);
        deletePortalUserMutation.mutate();
      });
    }
  };

  useEffect(() => {
    setPageNumber(1);
  }, [agentNextCallData?.callContactId]);

  const onClickBack = () => {
    setPageNumber(pageNumber - 1);
  };

  const onClickNext = () => {
    setPageNumber(pageNumber + 1);
  };

  const isCallActive = newCallId != null;
  const contactFullName = [agentNextCallData?.firstName, agentNextCallData?.lastName]
    .filter(Boolean)
    .join(' ');

  const _renderSubtitle = () => {
    if (isFetchingNextCall) return 'Loading next contact...';
    if (!contactFullName) return 'No contact assigned';
    if (isCallActive) return `In call with ${contactFullName}`;

    return `Next contact: ${contactFullName}`;
  };

  return (
    <div className="h-full bg-grey-brand-1 pb-8">
      <div className="px-8 pt-6 pb-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-blue-brand">Call Centre</h1>
              {isCallActive && (
                <span className="flex items-center gap-2 bg-green-100 text-green-800 text-xxs font-semibold uppercase tracking-wider rounded-full px-3 py-1">
                  <span className="w-2 h-2 rounded-full bg-green-600 animate-pulse" />
                  Call in progress
                </span>
              )}
            </div>
            <p className="text-xs text-text-grey mt-1">{_renderSubtitle()}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-8 items-center bg-white rounded-md shadow-sm px-3">
              <div className="flex items-baseline gap-2">
                <span className="text-base font-bold text-secondary leading-none">
                  {agentDailyCallsTotalData?.totalCount ?? 0}
                </span>
                <span className="text-xxs uppercase tracking-wider text-text-grey">
                  calls completed
                </span>
              </div>
            </div>
            <Button
              isLoading={isCreateCallLoading}
              onClick={_createCall}
              className="h-8 min-h-0 py-0 bg-primary hover:bg-primary-dark text-white text-xs font-semibold px-4 rounded-md"
              spinnerSize={10.5}
              disabled={isCallActive}
            >
              Call Now
            </Button>
            <Button
              isLoading={isLoadingCallLeftReport}
              onClick={downloadCallLeftReport}
              className="h-8 min-h-0 py-0 bg-secondary hover:bg-secondary-dark text-white text-xs font-semibold px-4 rounded-md"
              spinnerSize={10.5}
            >
              Call Left Report
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8 grid grid-cols-3 gap-5">
        <div className="col-span-2 flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-5">
            <Panel title="Contact" className="min-h-86">
              {!isFetchingNextCall ? (
                <EditCallCentreProfileForm
                  defaultValues={{
                    userId: agentNextCallData?.userId,
                    firstName: agentNextCallData?.firstName,
                    lastName: agentNextCallData?.lastName,
                    email: agentNextCallData?.email,
                    cellphoneNumber: agentNextCallData?.phoneNumber,
                    partner: agentNextCallData?.partner,
                  }}
                  onSubmit={_updateProfile}
                  onDelete={_deleteProfile}
                  isSubmitLoading={isProfileUpdateLoading}
                  displayDelete={isCallActive && !isFetchingNextCall}
                  isCallActive={isCallActive}
                />
              ) : (
                <PanelPlaceholder>
                  <Spinner />
                </PanelPlaceholder>
              )}
            </Panel>
            <Panel title="Call Outcome" allowOverflow>
              {isCallActive && !isFetchingNextCall ? (
                <CallOutcomeForm
                  defaultValues={{
                    callDate: new Date().toLocaleString(),
                    agentFullName: agentNextCallData?.agentFullName ?? '',
                    callOutcome: 0,
                    durationInSeconds: 0,
                    doNotContact: false,
                    callBack: false,
                  }}
                  onSubmit={_finishCall}
                  isSubmitLoading={isCreateCallLoading}
                  callOutcomeSelectValues={Object.keys(CallOutcomeDescriptionEnum)
                    .filter((key) => Number.isNaN(Number(key)))
                    .map((val, key) => {
                      return {
                        value: val,
                        key: `${key}`,
                      };
                    })}
                />
              ) : (
                <PanelPlaceholder>
                  <span>
                    Waiting for call to start.
                    <br />
                    Press <span className="font-semibold text-primary-dark">Call Now</span> to
                    begin.
                  </span>
                </PanelPlaceholder>
              )}
            </Panel>
          </div>
          <Panel title="General Notes" className="flex-grow">
            <div className="flex-grow min-h-24 overflow-y-auto p-4 text-sm whitespace-pre-line">
              {agentNextCallData?.notes || (
                <span className="text-xs text-text-grey">No notes for this contact.</span>
              )}
            </div>
          </Panel>
        </div>

        <Panel title="Call History">
          {!isFetchingCallHistory && callContactCallsData?.items ? (
            <div className="flex flex-col flex-grow">
              {callContactCallsData.items.length > 0 ? (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-grey-brand-1 text-text-grey">
                      <th className="px-3 py-2.5 text-left text-xxs font-semibold uppercase tracking-wider">
                        Date &amp; Time
                      </th>
                      <th className="px-3 py-2.5 text-left text-xxs font-semibold uppercase tracking-wider">
                        Agent
                      </th>
                      <th className="px-3 py-2.5 text-right text-xxs font-semibold uppercase tracking-wider">
                        Call Outcome
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {callContactCallsData.items.map((call, index) => (
                      <tr
                        key={`${call.callDate}-${call.agentFirstName}-${call.callOutcome}`}
                        className={index % 2 !== 0 ? 'bg-grey-brand-2' : ''}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          {dateTimeFormatterGmtZoneOffset(call.callDate, '/', 2)}
                        </td>
                        <td className="px-3 py-2">{call.agentFirstName}</td>
                        <td className="px-3 py-2 text-right">
                          <span
                            className={`inline-block rounded-full px-2 py-1 text-xxs font-semibold whitespace-nowrap ${callOutcomeBadgeStyle(
                              call.callOutcome as CallOutcome,
                            )}`}
                          >
                            {formatCallOutcome(call.callOutcome as CallOutcome)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <PanelPlaceholder>No calls found for this contact.</PanelPlaceholder>
              )}
              <ConditionalRenderer condition={callContactCallsData?.totalPages > 0}>
                <div className="flex items-center justify-between border-t border-grey-brand-3 mt-auto px-3 py-2">
                  <span className="text-xxs text-text-grey">
                    Page {callContactCallsData?.totalPages === 0 ? 0 : pageNumber} of{' '}
                    {callContactCallsData?.totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      type="text"
                      className="text-xs rounded-md"
                      onClick={onClickBack}
                      disabled={pageNumber === 1}
                    >
                      Back
                    </Button>
                    <Button
                      type="text"
                      className="text-xs rounded-md"
                      onClick={onClickNext}
                      disabled={pageNumber >= callContactCallsData?.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </ConditionalRenderer>
            </div>
          ) : (
            <PanelPlaceholder>
              <Spinner />
            </PanelPlaceholder>
          )}
        </Panel>
      </div>
    </div>
  );
};
